import { Form, FormField } from "components/ui/form";
import { ControllerRenderProps, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useState } from "react";
import { z } from "zod";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { InputFileDragGs, SelectGs } from "components/ui/Forms";
import DatePickerGs from "components/ui/Forms/datePickerGs";
import { Button } from "components/ui/ButtonGs";
import {
  Download,
  SquareMinus,
  ListChevronsDownUpIcon,
  ListChevronsUpDownIcon,
} from "lucide-react";
import { notify } from "services";
import { docFormSchema } from "./form";
import { format } from "date-fns";
import { DriverDoc, DriverDocPayload } from "interfaces/driver-doc";
import { useHubDriver } from "hooks/api";
import { toast } from "sonner";
import { useErrorHandler } from "exceptions/ErrorHandler";
import { useHubDriverDocTypes } from "hooks/api/Hub/useHubDriverDocTypes";
import { useHubS3 } from "hooks/api/Hub/useHubS3";
import { Box } from "components/ui/Box";
import { Modal } from "components/ui/Modal";
interface DocumentProps {
  docs: DriverDoc[];
  driverId: string | number;
}

export const Documents = ({ docs, driverId }: DocumentProps) => {
  const [currentFiles, setCurrentFiles] = React.useState<File[] | undefined>();

  const queryClient = useQueryClient();

  const [typeDocument, setTypeDocument] = useState<string>("");
  const [openModal, setOpenModal] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({}); // Controla o estado de expansão de cada grupo

  const { errorHandler } = useErrorHandler();
  const { fetchDocTypes } = useHubDriverDocTypes();
  const { addDriverDoc, removeDriverDoc } = useHubDriver();
  const { downloadFile } = useHubS3();

  const { data: docTypes, isLoading: loadinDocTypes } = useQuery({
    queryKey: ["getDriverDocTypes"],
    queryFn: () => fetchDocTypes(),
    staleTime: 1000 * 60 * 5, // 5 min,
  });

  const docForm = useForm<z.infer<typeof docFormSchema>>({
    resolver: zodResolver(docFormSchema),
  });

  const onSuccessCreate = () => {
    onResetDoc();
    setOpenModal(false);
  };

  const mutationAdd = useMutation(addDriverDoc, {
    onSuccess: () => {
      queryClient.refetchQueries("getListDriverDocs");
      toast.success("Documento adicionado com sucesso!");
      onSuccessCreate();
    },
    onError: (error) => {
      errorHandler(error);
    },
  });

  const mutationRemove = useMutation(removeDriverDoc, {
    onSuccess: () => {
      queryClient.refetchQueries("getListDriverDocs");
      toast.success("Documento removido com sucesso!");
    },
    onError: (error) => {
      errorHandler(error);
    },
    onSettled: () => {
      mutationRemove.reset();
    },
  });

  const mutationDownload = useMutation(downloadFile, {
    onError: (error) => {
      errorHandler(error);
    },
    onSettled: () => {
      mutationDownload.reset();
    },
  });

  const onSubmitDoc = (data: z.infer<typeof docFormSchema>) => {
    const { driver_doc_type_id, expiration_date } = data;

    const expirationRequired = docTypes?.data.some(
      ({ id, expiration }) =>
        id === Number(driver_doc_type_id) && Boolean(expiration)
    );

    if (expirationRequired && !Boolean(expiration_date)) {
      return docForm.setError("expiration_date", {
        message: "Obrigatório para esse tipo de documento",
      });
    }

    const driverDoc: DriverDocPayload = {
      driver_doc_type_id: Number(driver_doc_type_id),
      driver_id: Number(driverId),
      expiration_date: expiration_date
        ? format(expiration_date, "yyyy-MM-dd")
        : undefined,
      images: currentFiles as File[],
    };

    mutationAdd.mutate(driverDoc);
  };

  const onFileChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(target.files || []);
    if (files.length === 0) return;

    // Atualiza o valor do campo com uma string para cada arquivo
    const fileData = files.map(
      (file) => `${file.type}|${file.size}|${file.name}`
    );

    setCurrentFiles(files); // Agora guarda um array de arquivos
    docForm.setValue("images", fileData); //Atualiza o valor do campo com o array de strings
  };
  const onResetDoc = () => {
    docForm.reset();
    docForm.setValue("images", []);
    docForm.setValue("expiration_date", undefined as any);
    docForm.setValue("driver_doc_type_id", "");
  };

  // 🔹 Agrupa os documentos pelo tipo
  // Agrupa os documentos pelo tipo
  const groupedDocs = docs.reduce<Record<string, DriverDoc[]>>((acc, doc) => {
    const type = doc.document_type || "Sem tipo";
    if (!acc[type]) acc[type] = [];
    acc[type].push(doc);
    return acc;
  }, {});

  const toggleGroup = (type: string) => {
    setOpenGroups((prev) => ({ ...prev, [type]: !prev[type] }));
  };

  return (
    <div className="mt-6">
      <h6>Documentos</h6>
      <p className="text-sm text-gray-500 mb-5">
        Documentos relacionados ao motorista como CNH, MOP, ASO
      </p>
      <Form {...docForm}>
        <form onSubmit={docForm.handleSubmit(onSubmitDoc)}>
          <Box className="mt-6">
            <div className="flex items-end gap-4">
              <div className="flex-1">
                <FormField
                  name="driver_doc_type_id"
                  control={docForm.control}
                  render={({ field }) => (
                    <SelectGs
                      {...field}
                      label={"Tipo"}
                      onValueChange={(value: string) => {
                        field.onChange(value);
                        setTypeDocument(value);
                      }}
                      content={docTypes?.comboOptions || []}
                      isLoading={loadinDocTypes}
                      value={field.value ?? undefined}
                    />
                  )}
                />
              </div>

              <Button.Root
                className="w-[188px]"
                variant="primary"
                size="sm"
                disabled={!typeDocument}
                type="button"
                onClick={() => setOpenModal(true)}
              >
                Adicionar Documento
              </Button.Root>
            </div>
          </Box>

          <Modal isOpen={openModal} styleSize="w-[900px]">
            <div>
              <h2 className="text-lg font-semibold mb-1">
                Adicionar arquivo para:{" "}
                <span className="text-primary">
                  {
                    docTypes?.comboOptions.find(
                      (el) => String(el.value) === String(typeDocument)
                    )?.name
                  }
                </span>
              </h2>
              <p className="text-sm text-gray-500 mb-5">
                Upload dos arquivos referente ao tipo de documento.
              </p>
            </div>
            <FormField
              name="expiration_date"
              control={docForm.control}
              render={({ field }) => (
                <DatePickerGs
                  {...field}
                  fieldValue={field.value ? new Date(field.value) : undefined}
                  maxDate={new Date("3000-01-01")}
                  label="Data de validade"
                  fieldOnChange={field.onChange}
                  showMonthYearPicker
                />
              )}
            />

            <div className="mt-4">
              <FormField
                name="images"
                control={docForm.control}
                render={({ field }) => (
                  <InputFileDragGs
                    {...field}
                    onChange={(e) => {
                      onFileChange(e); // Transforma os arquivos e atualiza o campo
                    }}
                    multiple
                    accept=".jpg,.jpeg,.png"
                  />
                )}
              />

              <div className="flex justify-start w-full gap-4 mt-4">
                <Button.Root
                  variant="secondary"
                  size="sm"
                  type="button"
                  onClick={() => setOpenModal(false)}
                >
                  Cancelar
                </Button.Root>
                <Button.Root variant="primary" size="sm" type="submit">
                  Salvar
                </Button.Root>
              </div>
            </div>
          </Modal>
        </form>

        <Box className="mt-6 space-y-6">
          {Object.entries(groupedDocs).map(([type, docs]) => {
            const isOpen = openGroups[type];
            return (
              <div
                key={type}
                className="w-full bg-white rounded-xl shadow-sm border border-slate-100 p-4"
              >
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm text-slate-500">Tipo do documento</p>
                    <p className="text-base font-medium text-slate-800">
                      {type}
                    </p>
                  </div>

                  <button
                    onClick={() => toggleGroup(type)}
                    className="p-2 rounded-lg hover:bg-slate-100 transition"
                    title={isOpen ? "Encolher lista" : "Expandir lista"}
                  >
                    {isOpen ? (
                      <ListChevronsDownUpIcon className="w-5 h-5 text-slate-600" />
                    ) : (
                      <ListChevronsUpDownIcon className="w-5 h-5 text-slate-600" />
                    )}
                  </button>
                </div>

                {isOpen && (
                  <div className="w-full">
                    <table className="w-full text-sm mt-2 border-spacing-y-2 table-auto border-collapse">
                      <thead className="text-left text-slate-500">
                        <tr>
                          <th className="py-2 px-1">Id</th>
                          <th className="py-2 px-1">Nome do arquivo</th>
                          <th className="py-2 px-1 text-right">Ações</th>
                        </tr>
                      </thead>
                      <tbody className="[&>tr>td]:py-2 [&>tr>td]:px-1">
                        {docs.map((doc, index) => (
                          <tr
                            key={doc.id || index}
                            className="border-b border-slate-100 last:border-none"
                          >
                            <td>{index + 1}</td>
                            <td className="text-slate-700">{doc.file_name}</td>
                            <td>
                              <div className="flex justify-end gap-4">
                                <button
                                  className="flex items-center"
                                  title="Baixar documento"
                                  onClick={() =>
                                    mutationDownload.mutate(doc.file_name)
                                  }
                                >
                                  <Download
                                    color="#334155"
                                    className={`hover:stroke-slate-900 ${
                                      mutationDownload.variables ===
                                      doc.file_name
                                        ? "opacity-50"
                                        : ""
                                    }`}
                                  />
                                </button>
                                <button
                                  className="flex items-center"
                                  title="Remover documento"
                                  disabled={mutationRemove.isLoading}
                                  onClick={() => mutationRemove.mutate(doc.id)}
                                >
                                  <SquareMinus
                                    color="#b91c1c"
                                    className={`hover:stroke-red-600 ${
                                      mutationRemove.variables === doc.id
                                        ? "opacity-50"
                                        : ""
                                    }`}
                                  />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </Box>
      </Form>
    </div>
  );
};
